import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { formatDate } from '../../utils/display';

const Icons = {
  Menu: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  Search: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  Bell: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  )
};

export default function Topbar({ onMenuClick, sidebarOpen = false }) {
  const { user, logout, setActiveRole } = useAuth();
  const { announcements } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const notificationButtonRef = useRef(null);
  const profileButtonRef = useRef(null);
  const activeRole = user?.activeRole || user?.role;
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canSwitch = userRoles.includes('admin') && userRoles.includes('teacher');

  const getPageTitle = () => {
    const path = location.pathname.split('/').pop();
    const titles = {
      dashboard: 'Dashboard',
      courses: 'My Course',
      classroom: 'Resources',
      resources: 'Resources',
      assignments: 'Assignments',
      grades: 'My Grades',
      calendar: 'Calendar',
      messaging: 'Messages',
      announcements: 'Announcements',
      'study-log': 'Study Log',
      games: 'Rewards & Games',
      grading: 'Grading',
      content: 'Content',
      attendance: 'Attendance',
      students: 'Students',
      academics: 'Academics',
      applications: 'Applications',
      website: 'Website',
      advanced: 'Advanced',
      registration: 'Registration',
      profile: 'Profile',
      users: 'User Management',
      'admin-signup': 'Admin Sign Up',
      finance: 'Finance',
      orders: 'Store Orders',
      pipeline: 'Leads Pipeline',
      assets: 'Marketing Assets',
      systems: 'Systems'
    };
    if (location.pathname.startsWith('/student/courses/')) return 'My Course';
    return titles[path] || 'Dashboard';
  };

  const unreadCount = announcements.filter(a => !a.read).length;
  const pageTitle = getPageTitle();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!location.pathname.startsWith('/student/search')) return;
    const params = new URLSearchParams(location.search);
    setSearchQuery(params.get('q') || '');
  }, [location.pathname, location.search]);

  useEffect(() => {
    const closeMenus = (event) => {
      if (event.key === 'Escape') {
        if (showNotifications) notificationButtonRef.current?.focus();
        if (showProfile) profileButtonRef.current?.focus();
        setShowNotifications(false);
        setShowProfile(false);
      }
    };
    document.addEventListener('keydown', closeMenus);
    return () => document.removeEventListener('keydown', closeMenus);
  }, [showNotifications, showProfile]);

  useEffect(() => {
    document.title = `${pageTitle} | School of SHEM`;
  }, [pageTitle]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (activeRole !== 'student') return;
    const trimmed = searchQuery.trim();
    navigate(`/student/search?q=${encodeURIComponent(trimmed)}`);
  };

  const handleRoleSwitch = () => {
    const nextRole = activeRole === 'admin' ? 'teacher' : 'admin';
    setActiveRole(nextRole);
    setShowProfile(false);
    navigate(nextRole === 'admin' ? '/admin/dashboard' : '/teacher/dashboard');
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="menu-toggle" onClick={onMenuClick} aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={sidebarOpen} aria-controls="portal-navigation">
          <Icons.Menu />
        </button>
        <div>
          <div className="page-title">
            {pageTitle}
          </div>
          <div className="text-gray-500" style={{ fontSize: '0.8125rem' }}>
            Welcome back, {user?.firstName}
          </div>
        </div>
      </div>

      <div className="topbar-right">
        {activeRole === 'student' && <form className="search-box" onSubmit={handleSearchSubmit}>
          <Icons.Search />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search resources, assignments, messages..."
            aria-label="Search student content"
          />
        </form>}

        <div className="topbar-anchor">
          <button 
            className="notification-bell"
            ref={notificationButtonRef}
            onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
            aria-label={unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'}
            aria-expanded={showNotifications}
            aria-controls="portal-notifications"
            aria-haspopup="true"
          >
            <Icons.Bell />
            {unreadCount > 0 && <span className="notification-badge" />}
          </button>

          {showNotifications && (
            <div id="portal-notifications" className="topbar-popover topbar-notifications" role="region" aria-label="Notifications">
              <div className="topbar-popover-heading">
                <h2>Notifications</h2>
              </div>
              <div className="topbar-notification-list">
                {announcements.slice(0, 5).map(announcement => (
                  <article className="topbar-notification-item" key={announcement.id}>
                    <strong>{announcement.title || 'Announcement'}</strong>
                    <span>{[announcement.category, formatDate(announcement.date || announcement.createdAt)].filter(Boolean).join(' · ')}</span>
                  </article>
                ))}
                {announcements.length === 0 && <p className="topbar-empty">No new notifications.</p>}
              </div>
              {activeRole === 'student' && <div className="topbar-popover-footer">
                <button className="btn btn-primary btn-sm" onClick={() => { navigate('/student/announcements'); setShowNotifications(false); }}>View all notifications</button>
              </div>}
            </div>
          )}
        </div>

        <div className="topbar-anchor">
          <button 
            className="user-profile"
            ref={profileButtonRef}
            onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
            aria-label="Open account menu"
            aria-expanded={showProfile}
            aria-controls="portal-account-menu"
            aria-haspopup="menu"
          >
            <div className="user-avatar">
              {user?.avatar || user?.firstName?.charAt(0)}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.firstName} {user?.lastName}</div>
              <div className="user-role" style={{ textTransform: 'capitalize' }}>
                {activeRole === 'teacher' ? 'Lecturer' : activeRole}
              </div>
            </div>
            <Icons.ChevronDown />
          </button>

          {showProfile && (
            <div id="portal-account-menu" className="topbar-popover topbar-account-menu" role="menu">
              <div className="topbar-account-summary">
                <strong>
                  {user?.firstName} {user?.lastName}
                </strong>
                <span>
                  {user?.email}
                </span>
              </div>
              <div className="topbar-account-actions">
                {canSwitch && (
                  <button
                    className="nav-item"
                    role="menuitem"
                    onClick={handleRoleSwitch}
                  >
                    <Icons.Settings />
                    <span>Switch to {activeRole === 'admin' ? 'Lecturer' : 'Admin'}</span>
                  </button>
                )}
                {(activeRole === 'admin' || activeRole === 'student') && <button
                  className="nav-item"
                  role="menuitem"
                  onClick={() => { navigate(activeRole === 'admin' ? '/admin/advanced' : '/student/profile'); setShowProfile(false); }}
                >
                  <Icons.Settings />
                  <span>{activeRole === 'admin' ? 'Advanced' : 'Profile'}</span>
                </button>}
                <button
                  className="nav-item is-danger"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
